/**
 * StreetSafetyOverlay - Colors ACTUAL roads based on Neural Index safety
 * Uses Mapbox's road vector tiles - colors real streets, not a grid
 * Like Waze traffic but for air quality
 */

import { useEffect } from 'react';
import { useMap } from 'react-map-gl';
import { useLocationStore } from '@/stores/useLocationStore';
import { useAirQualityStore } from '@/stores/useAirQualityStore';

interface StreetSafetyOverlayProps {
  visible?: boolean;
}

/**
 * Get color based on Neural Index
 * Green (safe) -> Yellow (moderate) -> Orange (threat) -> Red (danger)
 */
function getNIColor(ni: number): string {
  if (ni >= 80) return '#00ff88'; // Bright green - SAFE
  if (ni >= 70) return '#7fff00'; // Yellow-green
  if (ni >= 60) return '#ffdd00'; // Yellow - MODERATE
  if (ni >= 50) return '#ffaa00'; // Orange
  if (ni >= 40) return '#ff6b35'; // Orange-red - THREAT
  return '#ff3366'; // Red - DANGER
}

export function StreetSafetyOverlay({ visible = true }: StreetSafetyOverlayProps) {
  const { current: mapRef } = useMap();
  const currentPosition = useLocationStore((state) => state.currentPosition);
  const neuralIndex = useAirQualityStore((state) => state.neuralIndex);

  useEffect(() => {
    if (!mapRef || !visible || !currentPosition) return;

    const map = mapRef.getMap();
    if (!map || !map.isStyleLoaded()) return;

    // Road layer IDs in Mapbox navigation-night-v1 style
    // These are the actual road layers we'll color
    const roadLayers = [
      'road-motorway',
      'road-motorway-trunk',
      'road-trunk',
      'road-primary',
      'road-secondary-tertiary',
      'road-street',
      'road-minor',
      'road-path',
      'road-pedestrian',
      'road-construction',
      'tunnel-motorway',
      'tunnel-trunk',
      'tunnel-primary',
      'tunnel-secondary-tertiary',
      'tunnel-street',
      'bridge-motorway',
      'bridge-trunk',
      'bridge-primary',
      'bridge-secondary-tertiary',
      'bridge-street',
    ];

    // Color roads based on their class + Neural Index
    // Major roads (highways) get penalty, minor roads near parks get bonus
    const getColorExpression = (): mapboxgl.Expression => {
      const baseColor = getNIColor(neuralIndex);

      // Create color expression based on road class
      // Highways/motorways tend to have worse air quality
      // Residential/paths tend to have better air quality
      return [
        'match',
        ['get', 'class'],
        // Highways - apply penalty
        ['motorway', 'motorway_link', 'trunk', 'trunk_link'],
        getNIColor(Math.max(20, neuralIndex - 25)),
        // Primary roads - slight penalty
        ['primary', 'primary_link'],
        getNIColor(Math.max(20, neuralIndex - 15)),
        // Secondary roads - minor penalty
        ['secondary', 'secondary_link', 'tertiary', 'tertiary_link'],
        getNIColor(Math.max(20, neuralIndex - 8)),
        // Residential/local streets - base NI
        ['street', 'street_limited'],
        getNIColor(neuralIndex),
        // Paths, pedestrian, service roads - slight bonus (away from traffic)
        ['path', 'pedestrian', 'footway', 'cycleway', 'service'],
        getNIColor(Math.min(100, neuralIndex + 10)),
        // Default
        baseColor,
      ] as mapboxgl.Expression;
    };

    // Apply coloring to each road layer
    roadLayers.forEach((layerId) => {
      if (map.getLayer(layerId)) {
        try {
          // Set the line color based on road class and NI
          map.setPaintProperty(layerId, 'line-color', getColorExpression());

          // Make roads slightly wider for better visibility
          const currentWidth = map.getPaintProperty(layerId, 'line-width');
          if (typeof currentWidth === 'number' && currentWidth < 4) {
            map.setPaintProperty(layerId, 'line-width', currentWidth * 1.3);
          }

          // Increase opacity for better visibility
          map.setPaintProperty(layerId, 'line-opacity', 0.9);
        } catch {
          // Layer might not support these properties, skip silently
        }
      }
    });

    // Add glow effect layers for major roads
    const addGlowLayer = (id: string, filter: mapboxgl.Expression) => {
      if (map.getLayer(id)) {
        map.removeLayer(id);
      }

      // Only add if the source exists
      if (!map.getSource('composite')) return;

      try {
        map.addLayer({
          id,
          type: 'line',
          source: 'composite',
          'source-layer': 'road',
          filter,
          paint: {
            'line-color': getColorExpression(),
            'line-width': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10, 4,
              14, 10,
              18, 20,
            ],
            'line-opacity': 0.3,
            'line-blur': 4,
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
        }, 'road-label'); // Add below road labels
      } catch {
        // Silently fail if layer can't be added
      }
    };

    // Add glow for major roads
    addGlowLayer('road-safety-glow-major', [
      'match',
      ['get', 'class'],
      ['motorway', 'trunk', 'primary'],
      true,
      false,
    ]);

    // Add glow for secondary roads
    addGlowLayer('road-safety-glow-secondary', [
      'match',
      ['get', 'class'],
      ['secondary', 'tertiary', 'street'],
      true,
      false,
    ]);

    // Cleanup function
    return () => {
      if (map && map.getStyle()) {
        // Remove glow layers on cleanup
        ['road-safety-glow-major', 'road-safety-glow-secondary'].forEach((id) => {
          if (map.getLayer(id)) {
            map.removeLayer(id);
          }
        });
      }
    };
  }, [mapRef, visible, currentPosition, neuralIndex]);

  // This component doesn't render anything directly - it modifies existing map layers
  return null;
}

export default StreetSafetyOverlay;
